import { FC, useContext, useEffect, useRef, useState } from "react";
import { useFetch } from "Hooks";
import { CaseContext, TechnologyContext } from "Stores";
import { TTechnologyCountDTO, TUseFetch } from "Types";
import { TitledChild, SquareTabContainer } from "Components";
import { TechnologyDocuments } from "Components/Case/Results/TechnologyDocuments";
import { TechnologyActionTypeEnum } from "Enums";
import { ISetTechnologyCountsAction } from "Interfaces/TechnologyActions";
import { TechnologyRequirements } from "Components/Case/Results/TechnologyRequirements";
import { ConceptTechnologyHeader } from "Components/Case/Results/ConceptTechnologyHeader";
import { ConceptTechnologyFooter } from "Components/Case/Results/ConceptTechnologyFooter";
import { TechnologyPartners } from "../TechnologyPartners";
import { TechnologyMedia } from "../TechnologyMedia/TechnologyMedia";
import { TechnologyHighlights, TechnologyHighlightsPreview } from "../TechnologyHighlights";
import { TechnologyOrConceptDescription } from "../TechnologyOrConceptDescription";
// styles
import styles from "./selectedTechnology.module.scss";
import { DeliveryLayoutContext } from "Providers";
import { TechnologySuppliers } from "../TechnologySuppliers";

export const SelectedTechnology: FC = () => {
    // Refs
    const currentScrollingElementRef = useRef<HTMLDivElement>(null);
    // Contexts
    const { caseState } = useContext(CaseContext);
    const { technologyState, technologyDispatch } = useContext(TechnologyContext);
    const {setCurrentMainScrolledContainer} = useContext(DeliveryLayoutContext);

    // State
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { fetchedData }: TUseFetch<TTechnologyCountDTO> = useFetch(`api/technologies/${caseState.selectedTechnologyId}/counts`);

    useEffect(() => {
        if(fetchedData) technologyDispatch({ type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: fetchedData } as ISetTechnologyCountsAction);
    }, [fetchedData, technologyDispatch]);

    useEffect(() => {
        setCurrentMainScrolledContainer(currentScrollingElementRef);
    }, [currentScrollingElementRef, setCurrentMainScrolledContainer]);

    if(!caseState.selectedTechnologyId) {
        return(<></>);
    } else {
        const documentCount = technologyState.counts.magPatentCount + technologyState.counts.patentArticleCount +
            technologyState.counts.magScienceCount + technologyState.counts.webArticleCount;

        let amountOfTabsOpen = 0;
        if(documentCount > 0) amountOfTabsOpen++;
        if(technologyState.counts.technologyHighlightsCount > 0) amountOfTabsOpen++;
        if(technologyState.counts.partnerCount > 0) amountOfTabsOpen++;
        if(technologyState.counts.requirementCount > 0) amountOfTabsOpen++;

        return (
            <>
                <ConceptTechnologyHeader />
                <div className={[styles.conceptTechnologyContentContainer, styles.scrollbarCustomThin].join(" ")} ref={currentScrollingElementRef}>
                    <div className={styles.conceptTechnologyOverviewContainer}>
                        <TechnologyMedia />
                        <TechnologyOrConceptDescription typeId={caseState.selectedTechnologyId} originalId={caseState.selectedOriginalTechnologyId} typeName="technologies" />
                        {technologyState.counts.technologyHighlightsCount > 0 ? 
                            <TechnologyHighlightsPreview technologyId={caseState.selectedTechnologyId} amount={3} onMoreButtonClick={ () => { setSelectedTabIndex(1); } } />
                            : null }
                    </div>

                    {!caseState.isDynamicDelivery && amountOfTabsOpen === 0 ? 
                        <></> :
                        !caseState.isDynamicDelivery && amountOfTabsOpen === 1 ?
                        <div>
                            {documentCount > 0 ? <><h4>Documents</h4><TechnologyDocuments /></> : null}
                            {technologyState.counts.technologyHighlightsCount > 0 ? <><h4>Highlights</h4><TechnologyHighlights /></> : null} 
                            {technologyState.counts.partnerCount > 0 ? <><h4>Partners</h4><TechnologyPartners /></> : null}
                            {technologyState.counts.requirementCount > 0 ? <><h4>Requirements</h4><TechnologyRequirements /></> : null}
                        </div>
                        :
                        <SquareTabContainer
                            parentallyIssuedSelectedTabIndex={selectedTabIndex}
                            setParentallyIssuedSelectedTabIndex={setSelectedTabIndex}
                            extraClassNameTabContentContainer={styles.squareTabContainerContent}
                        >
                            { caseState.isDynamicDelivery || documentCount > 0 ?
                                <TitledChild title={`Documents (${documentCount})`}>
                                    <TechnologyDocuments />
                                </TitledChild>
                                : null }
                            { caseState.isDynamicDelivery || technologyState.counts.technologyHighlightsCount > 0 ?
                                <TitledChild title={`Highlights (${technologyState.counts.technologyHighlightsCount})`}>
                                    <TechnologyHighlights />
                                </TitledChild>
                                : null }
                            { caseState.isDynamicDelivery || technologyState.counts.partnerCount > 0 ?
                                <TitledChild title={`Organizations (${technologyState.counts.partnerCount})`}>
                                    <TechnologyPartners />
                                </TitledChild> 
                                : null }
                            { caseState.isDynamicDelivery || technologyState.counts.requirementCount > 0 ?
                                <TitledChild title={`Requirements (${technologyState.counts.requirementCount})`}>
                                    <TechnologyRequirements />
                                </TitledChild>
                                : null }
                            { caseState.isDynamicDelivery || technologyState.counts.supplierCount > 0 ?
                                <TitledChild title={`Suppliers (${technologyState.counts.supplierCount})`}>
                                    <TechnologySuppliers />
                                </TitledChild>
                                : null }
                        </SquareTabContainer>
                    }
                </div>
                <ConceptTechnologyFooter />
            </>
        );
    }
};